// JWT decode / expiry utilities for the Session_Store.
//
// These helpers read the JWT payload purely for client-side UX gating
// (deciding whether the session looks authenticated). The token signature is
// NOT verified here — the server remains the sole authority on token validity
// (Requirement 11.5). Only the `exp` claim is consumed by callers.

// Decode a base64url-encoded string into its raw UTF-8 form.
// Throws if the input is not valid base64 once normalised.
function base64UrlDecode (segment) {
  // base64url uses `-`/`_` in place of `+`/`/` and omits padding.
  let normalised = segment.replace(/-/g, '+').replace(/_/g, '/')
  const paddingNeeded = normalised.length % 4
  if (paddingNeeded === 2) {
    normalised += '=='
  } else if (paddingNeeded === 3) {
    normalised += '='
  } else if (paddingNeeded === 1) {
    // A remainder of 1 is never valid base64.
    throw new Error('Invalid base64url length')
  }
  return atob(normalised)
}

// Decode the payload (second segment) of a JWT.
// Returns the parsed payload object, or `null` when the token is missing or
// malformed. No signature verification is performed.
export function decodeJwtPayload (token) {
  if (typeof token !== 'string') {
    return null
  }

  const parts = token.split('.')
  if (parts.length !== 3 || parts[1] === '') {
    return null
  }

  try {
    const json = base64UrlDecode(parts[1])
    const payload = JSON.parse(json)
    // A JWT payload must be a JSON object (not an array, number, etc.).
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
      return null
    }
    return payload
  } catch (e) {
    return null
  }
}

// Determine whether a token should be treated as expired.
// Returns `true` for a null/malformed token or a missing/non-numeric `exp`
// claim; otherwise compares the `exp` claim (seconds since epoch) against the
// supplied current time in milliseconds.
export function isTokenExpired (token, nowMs = Date.now()) {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
    return true
  }
  return payload.exp * 1000 <= nowMs
}
